import { useEffect } from 'react';

export type MeldungsArt = 'success' | 'error' | 'info';

export interface MeldungsInhalt {
  art: MeldungsArt;
  text: string;
}

/**
 * Rückmeldung nach einer Aktion, fixiert am unteren Rand (§6 Design-System).
 *
 * Vorher standen Erfolg und Fehler oben über den Karten, die auslösenden
 * Knöpfe aber in einer langen Liste darunter. Wer unten klickte, sah nie
 * etwas — von „der Knopf tut nichts" nicht zu unterscheiden. Genau das war
 * der Befund im Admin-Panel.
 *
 * **Fehler verschwinden nicht von allein.** Ein übersehener Fehler führt
 * dazu, dass man die Aktion für erledigt hält — beim Sperren eines Zugangs
 * ist das der teuerste Irrtum, den diese Oberfläche zulässt. Erfolg
 * verschwindet nach sechs Sekunden, damit man nicht bei jedem Schritt
 * wegklicken muss.
 */
export function Meldung({
  inhalt,
  onClose,
}: {
  inhalt: MeldungsInhalt | null;
  onClose: () => void;
}) {
  const art = inhalt?.art;

  useEffect(() => {
    if (art !== 'success' && art !== 'info') return;
    const timer = setTimeout(onClose, 6000);
    return () => clearTimeout(timer);
    // `inhalt.text` gehört in die Abhängigkeiten: zwei Erfolge nacheinander
    // sollen die Frist neu starten, nicht die erste weiterlaufen lassen.
  }, [art, inhalt?.text, onClose]);

  if (!inhalt) return null;

  return (
    <div
      className={`alert alert--toast alert--${inhalt.art}`}
      role={inhalt.art === 'error' ? 'alert' : 'status'}
    >
      <span className="alert__text">{inhalt.text}</span>
      <button
        type="button"
        className="alert__close"
        onClick={onClose}
        aria-label="Meldung schließen"
      >
        ×
      </button>
    </div>
  );
}
